import { ISearchParameters, ITableReport } from "./TableInterfaces";
import { DeltaHedgeOrderType } from "./deltaHedgeOrder";
import { DropdownItem } from "./dropdownInterfaces";
import { StepOrderSide } from "./stepInterfaces";

export interface IDeltaHedgeAccountFormValues {
  sahraUserId: number;
  amount: number;
  deltaHedgePercent: number;
  flexibilityPercent: number;
  partialFlexibilityPercent: number;
  insValues: { [name: string]: string };
  mode: DeltaHedgeAccountsUploadFileMode;
  minLastTradeVolume: number;
}

export enum DeltaHedgeAccountsUploadFileMode {
  UpdateAll = 1,
  ExistingItemCorrection = 2,
}

export interface IDeltaHedgeAccountSearchParam extends ISearchParameters {}
export interface IDeltaHedgeAccountAggregateReportSearchParam extends ISearchParameters {
  groupId: number;
}

export interface IDeltaHedgeAccountAggregateReport extends ITableReport {
  accountName: string;
  accountId: number;
  groupId: number;
  deltaIndex1PercentPortfolio: number;
  gammaIndex1PercentPortfolio: number;
  thetaPortfolio: number;
  vegaPortfolio: number;
  investedCapital: number;
  deltaPercentage: number;
  deltaPercentageStr: string;
  gammaPercentage: number;
  gammaPercentageStr: string;
  lockedProfitPortfolio: number;
  dailyLockedProfitPortfolio: number;
  dailyLockedEfficiency: string;
  normalBubblePortfolio: number;
  totalBuyPositionWeight: number;
  sumRemaingDay_BuyPosition: number;
  totalSellPositionWeight: number;
  sumRemainingDay_SellPosition: number;
  weightedAvgBuyPositionRemaingDay: number;
  weightedAvgSellPositionRemaingDay: number;
  groupValue: number;
  portfoValue: number;
  dei: number;
  te: number;
  deltahedgeAccountId: number;
}

export interface IDeltaHedgeAccountChartsChartSearchParam {
  fromVolatility: number;
  toVolatility: number;
  pointCount: number;
  chartType: number;
  sahraUserId: number;
  groupId: number;
  deltahedgeAccountId: number;
}

export interface IDeltaHedgeAccountDesiredBoundryChartSearchParam {
  deltahedgeAccountId: number;
  sahraUserId: number;
  dei: number;
  te: number;
  groupId: number;
}

export interface IDeltaHedgeAccountChartsReport extends ITableReport {
  point: number;
  dailyLockedProfitPortfolio: number;
  gamaPercent: number;
  deltaPercent: number;
  isCurrent: boolean;
}

export interface IDeltaHedgeAccountDetailReportSearchParam extends ISearchParameters {
  groupId: number;
  accountId: number;
}

export interface IDeltaHedgeAccountTableReport extends ITableReport {
  account_id: number;
  deltahedegAccountId: number;
  strategy?: string;
  strategyId?: number;
}

export interface IInsValues extends DropdownItem {
  value: string;
}

export interface IPairPositionReport extends ITableReport {
  priceValue: string;
  instrumentName1: string;
  instrumentId1: string;
  orderSide1: string;
  instrumentName2: string;
  instrumentId2: string;
  orderSide2: string;
  orderSideValue1: number;
  orderSideValue2: number;
  positionType1: string;
  group: string;
  optionType1: string;
  optionType2: string;
  volume1: string;
  volume2: string;
  yesterDayVolume1Str: string;
  yesterDayVolume1: number;
  yesterDayVolume2Str: string;
  yesterDayVolume2: number;
  lastPrice1: string;
  lastPrice2: string;
  blackScholes1: string;
  blackScholes2: string;
  baseLastPrice1: string;
  baseLastPrice2: string;
  remainingDay1: string;
  remainingDay2: string;
  contractSize1: string;
  contractSize2: string;
  volatilityBaseIns1: string;
  volatilityBaseIns2: string;
  relativeTurbulence1: string;
  relativeTurbulence2: string;
  delta1: string;
  delta2: string;
  gama1: string;
  gama2: string;
  iv1: string;
  iv2: string;
  indicatorDeltaPercentage1: string;
  indicatorDeltaPercentage2: string;
  indicatorGamaPercentage1: string;
  indicatorGamaPercentage2: string;
  gamaInTermsOfDeltaPercentage1: string;
  gamaInTermsOfDeltaPercentage2: string;
  dailyLockProfit1: string;
  dailyLockProfit2: string;
  involvedFund1: string;
  involvedFund2: string;
  normalBubble1: string;
  normalBubble2: string;
  bubble1: string;
  bubble2: string;
  hedgeFactor: string;
  remainingDayStr: string;
  remainingDay: number;
  dailyLockProfit: string;
  involvedFund: string;
  dailyLockProfitPercentStr: string;
  dailyLockProfitPercent: number;
  gamaInTermsOfDeltaPercentage: string;
  gamaPercent: string;
  hedgeFactorChange: string;
  relativeNormalBubble: string;
  absBubbleDiff: string;
}

export interface IChartCommonSearchParam {
  fromDate: string;
  toDate: string;
}

export interface IAddDeltaHedgeAccount {
  deltahedgeAccountId?: number;
  accountId: number;
  strategyId?: number;
  backtestProfileId?: number;
  selectionType?: SelectionType;
  isManual?: boolean;
}

export interface IPortfoPairPositionSearchParam extends ISearchParameters {
  userIds: number[];
  positionType: string[];
  groupId: number[];
  sameDueDate: boolean;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  minYesterdayValue: number;
  minLastTradeValue: number;
}

export interface IPortfoPairPositionFilterSetting {
  userIds: number[];
  positionType: string[];
  groupId: number[];
  sameDueDate: boolean;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  minYesterdayValue: number;
  minLastTradeValue: number;
}

export interface IRolePairPositionSearchParam extends IPortfoPairPositionSearchParam {
  userIds: number[];
  positionType: string[];
  groupId: number[];
  sameDueDate: boolean;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  extend: boolean;
  minOffsetRemainingDay: number;
  minOpenRemainingDay: number;
  maxOffsetRemainingDay: number;
  maxOpenRemainingDay: number;
}

export interface IRolePairPositionFilterSetting {
  userIds: number[];
  positionType: string[];
  groupId: number[];
  sameDueDate: boolean;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  extend: boolean;
  minOffsetRemainingDay: number;
  minOpenRemainingDay: number;
  maxOffsetRemainingDay: number;
  maxOpenRemainingDay: number;
  minYesterdayValue: number;
  minLastTradeValue: number;
}

export interface IRolePairPositionTableReport extends IPortfoPairPositionTableReport {
  openOrderExpr: string;
  openOrder: string;
  yesterDayValue1Str: string;
  yesterDayValue1: number | null;
  yesterDayValue2Str: string;
  yesterDayValue2: number | null;
  vega1: string;
  vega2: string;
  theta1: string;
  theta2: string;
  indicatorVegaPercentage1: string;
  indicatorVegaPercentage2: string;
  stageToPrice1: string;
  stageToPrice2: string;
  involvedFundLive: string;
  rangeIv: string;
  dailyLockLt: string;
  dailyIndexVolatility: string;
  indexPnl: string;
  indexPnl1Percent: string;
  dailyLockProfitSTPercentStr: string;
  totalLockProfitLTPercentStr: string;
  positionGama: string;
  positionTheta: string;
  thetaPercent: string;
  rehedgeRatio: string;
  dei: string;
  hedgingErrorStd: string;
  teHeStd: string;
  tePnL1HeStd: string;
}

export interface IPortfoPairPositionTableReport extends ITableReport {
  openOrderExpr: string;
  openOrder: string;
  username: string;
  hash: string;
  instrumentName1: string;
  instrumentCode1: string;
  instrumentId1: string;
  orderSide1: string;
  orderSide1Enum: StepOrderSide;
  instrumentName2: string;
  instrumentCode2: string;
  instrumentId2: string;
  orderSide2: string;
  orderSide2Enum: StepOrderSide;
  positionType1: string;
  group: string;
  optionType1: DeltaHedgeOrderType;
  optionType2: DeltaHedgeOrderType;
  volume1: string;
  volume2: string;
  yesterDayVolume1Str: string;
  yesterDayVolume1: number;
  yesterDayVolume2Str: string;
  yesterDayVolume2: number;
  lastPrice1: string;
  lastPrice2: string;
  blackScholes1: string;
  blackScholes2: string;
  baseLastPrice1: string;
  baseLastPrice2: string;
  remainingDay1: string;
  remainingDay2: string;
  contractSize1: string;
  contractSize2: string;
  volatilityBaseIns1: string;
  volatilityBaseIns2: string;
  relativeTurbulence1: string;
  relativeTurbulence2: string;
  delta1: string;
  delta2: string;
  gama1: string;
  gama2: string;
  iv1: string;
  iv2: string;
  indicatorDeltaPercentage1: string;
  indicatorDeltaPercentage2: string;
  indicatorGamaPercentage1: string;
  indicatorGamaPercentage2: string;
  gamaInTermsOfDeltaPercentage1: string;
  gamaInTermsOfDeltaPercentage2: string;
  dailyLockProfit1: string;
  dailyLockProfit2: string;
  involvedFund1: string;
  involvedFund2: string;
  normalBubble1: string;
  normalBubble2: string;
  bubble1: string;
  bubble2: string;
  hedgeFactor: string;
  offsetHedgeFactor: string;
  remainingDayStr: string;
  remainingDay: number;
  dailyLockProfit: string;
  involvedFund: string;
  dailyLockProfitPercentStr: string;
  priceValue: string;
  offsetPriceValue: string;
  dailyLockProfitPercent: number;
  gamaInTermsOfDeltaPercentage: string;
  gamaPercent: string;
  hedgeFactorChange: string;
  relativeNormalBubble: string;
  absBubbleDiff: string;
  users: IPortfoPairPositionUser[];
  priceValueInstantOffset: string;
  dailyLockProfitPercentInstantOffset: number | null;
  offsetSide: number;
}

export interface IPortfoPairPositionUser {
  userId: string;
  userName: string;
  relatedPortfo: {
    instrumentVolume1: number;
    instrumentVolume2: number;
  };
}

export interface StrategyReport extends ITableReport {
  strategyName: string;
  suffix: string;
  tradebase: boolean;
  buyCall: boolean;
  sellCall: boolean;
  buyPut: boolean;
  sellPut: boolean;
}

export interface IStrategySearchParam extends ISearchParameters {}

export interface IStrategyDto {
  id?: number;
  strategyName: string;
  suffix: string;
  tradebase: boolean;
  buyCall: boolean;
  sellCall: boolean;
  buyPut: boolean;
  sellPut: boolean;
}

export enum SelectionType {
  Manual = "Manual",
  Calculational = "Calculational",
}

interface CommentItem {
  label: string;
  value: string;
}

interface Point {
  x: number;
  y: number;
  commentItems: CommentItem[];
}

interface Series {
  serieName: string;
  points: Point[];
}

export interface ChartData {
  titleX: string;
  titleY: string;
  series: Series[];
}
